import React, { useEffect, useState } from "react";
import styled, { css, keyframes } from "styled-components";
import { FaTimes } from "react-icons/fa";
import { AnimatePresence, motion } from "framer-motion";
import { FiEdit, FiShoppingCart, FiTrash2 } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { priceFormatter } from "../../utils/utils";
import { flyToCartFromImage } from "../../utils/flyToCart";
import CustomToast from "./CustomToast";
import { deleteProduct } from "../../store/slices/products/asyncThunks";
import { useDispatch, useSelector } from "react-redux";
import { selectProducts } from "../../store/slices/products/slice";
import { FRONT_URL } from "../../utils/constants";
import { addToCart } from "../../store/slices/cart/slice";
import ImagePlaceholder from "./ImagePlaceholder";
import AdminToast from "./AdminToast";

const flyToCart = (e) => {
  const card = e.currentTarget.closest("[data-card]");
  flyToCartFromImage(card?.querySelector("img"));
};

const ProductCard = ({ product, small, shop, admin, onEdit, setForm }) => {
  const products = useSelector(selectProducts);
  const dispatch = useDispatch();
  const [showToast, setShowToast] = useState(false);
  const [shake, setShake] = useState(false);
  const [openRemoveModal, setOpenRemoveModal] = useState(false);
  const [adminToast, setAdminToast] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [imageLoaded, setImageLoaded] = useState(false);

  const navigate = useNavigate();

  const incrementQty = () => setQuantity((prev) => prev + 1);
  const decrementQty = () => setQuantity((prev) => (prev > 1 ? prev - 1 : 1));

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape" && modalOpen) {
        setModalOpen(false);
      }
      if (e.key === "Escape" && openRemoveModal) {
        setOpenRemoveModal(false);
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [modalOpen, openRemoveModal]);

  useEffect(() => {
    if (modalOpen || openRemoveModal) {
      document.documentElement.style.overflow = "hidden";
    } else {
      document.documentElement.style.overflow = "auto";
    }
  }, [modalOpen, openRemoveModal]);

  return (
    <>
      {small ? (
        <Card
          $small
          onClick={() => {
            if (!admin) {
              navigate(`/proizvod/${product?.id}`);
              setTimeout(() => {
                window.scrollTo(0, 0);
              }, 300);
            }
          }}
        >
          <ImageWrapper>
            {product?.thumbnailImage && !imageLoaded && (
              <LoaderOverlay>
                <l-line-spinner
                  size="16"
                  stroke="2"
                  speed="1"
                  color="#fe5f55"
                />
              </LoaderOverlay>
            )}

            {product?.thumbnailImage ? (
              <Image
                src={`${FRONT_URL}/thumbnails/${product?.thumbnailImage}`}
                alt={product?.name}
                onLoad={() => setImageLoaded(true)}
                onError={() => setImageLoaded(true)}
              />
            ) : (
              <ImagePlaceholder iconSize={36} />
            )}

            {product?.badge && <Badge>{product?.badge}</Badge>}
            <ColorDot $color={product?.color} />
          </ImageWrapper>
          <Info>
            <Title>{product?.name}</Title>

            <Column>
              <Stock $available={product?.isInStock}>
                {product?.comingSoon
                  ? "Uskoro"
                  : product?.isInStock
                  ? "Na stanju"
                  : "Rasprodato"}
              </Stock>
              {product?.comingSoon ? (
                <Price>Uskoro u prodaji</Price>
              ) : product?.salePrice ? (
                <PriceRow>
                  <PriceOld>{priceFormatter(product?.price)} RSD</PriceOld>
                  <Price>{priceFormatter(product?.salePrice)} RSD</Price>
                </PriceRow>
              ) : (
                <Price>{priceFormatter(product?.price)} RSD</Price>
              )}
            </Column>
          </Info>
          <ButtonContainer>
            <AddToCart
              $small
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/proizvod/${product?.id}`);
                setTimeout(() => {
                  window.scrollTo(0, 0);
                }, 300);
              }}
            >
              Prikaži proizvod
              <i className="ar">→</i>
            </AddToCart>
          </ButtonContainer>
        </Card>
      ) : (
        <Card
          data-card="true"
          onClick={() => {
            if (!admin) {
              navigate(`/proizvod/${product?.id}`);
              setTimeout(() => {
                window.scrollTo(0, 0);
              }, 300);
            }
          }}
        >
          <ImageWrapper>
            {product?.thumbnailImage && !imageLoaded && (
              <LoaderOverlay>
                <l-line-spinner
                  size="16"
                  stroke="2"
                  speed="1"
                  color="#fe5f55"
                />
              </LoaderOverlay>
            )}

            {product?.thumbnailImage ? (
              <Image
                src={`${FRONT_URL}/thumbnails/${product?.thumbnailImage}`}
                alt={product?.name}
                onLoad={() => setImageLoaded(true)}
                onError={() => setImageLoaded(true)}
              />
            ) : (
              <ImagePlaceholder iconSize={36} />
            )}

            {product?.badge && <Badge>{product?.badge}</Badge>}
            {admin ? (
              <Icons>
                <Edit
                  size={18}
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit();
                    setForm(product);
                  }}
                />
                <Trash
                  size={18}
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenRemoveModal(true);
                  }}
                />
              </Icons>
            ) : null}
            <ColorDot $color={product?.color} />
          </ImageWrapper>
          <Info>
            <Title>{product?.name}</Title>
            <Description>{product?.description}</Description>

            <Column>
              <Stock $available={product?.isInStock}>
                {product?.comingSoon
                  ? "Uskoro"
                  : product?.isInStock
                  ? "Na stanju"
                  : "Rasprodato"}
              </Stock>
              {product?.comingSoon ? (
                <Price>Uskoro u prodaji</Price>
              ) : product?.salePrice ? (
                <PriceRow>
                  <PriceOld>{priceFormatter(product?.price)} RSD</PriceOld>
                  <Price>{priceFormatter(product?.salePrice)} RSD</Price>
                </PriceRow>
              ) : (
                <Price>{priceFormatter(product?.price)} RSD</Price>
              )}
            </Column>
            {admin ? (
              <Sizes $admin>
                {product?.sizeS && <Size>S</Size>}
                {product?.sizeM && <Size>M</Size>}
                {product?.sizeL && <Size>L</Size>}
                {product?.sizeXL && <Size>XL</Size>}
                {product?.sizeSM && <Size>S/M</Size>}
                {product?.sizeLXL && <Size>L/XL</Size>}
                {product?.socksS && <Size>39-42</Size>}
                {product?.socksM && <Size>43-46</Size>}
              </Sizes>
            ) : (
              <Sizes $shake={shake}>
                {product?.sizeS && (
                  <Size
                    $active={selectedSize === "S"}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedSize("S");
                    }}
                  >
                    S
                  </Size>
                )}
                {product?.sizeM && (
                  <Size
                    $active={selectedSize === "M"}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedSize("M");
                    }}
                  >
                    M
                  </Size>
                )}
                {product?.sizeL && (
                  <Size
                    $active={selectedSize === "L"}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedSize("L");
                    }}
                  >
                    L
                  </Size>
                )}
                {product?.sizeXL && (
                  <Size
                    $active={selectedSize === "XL"}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedSize("XL");
                    }}
                  >
                    XL
                  </Size>
                )}
                {product?.sizeSM && (
                  <Size
                    $active={selectedSize === "S/M"}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedSize("S/M");
                    }}
                  >
                    S/M
                  </Size>
                )}
                {product?.sizeLXL && (
                  <Size
                    $active={selectedSize === "L/XL"}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedSize("L/XL");
                    }}
                  >
                    L/XL
                  </Size>
                )}
                {product?.socksS && (
                  <Size
                    $active={selectedSize === "39-42"}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedSize("39-42");
                    }}
                  >
                    39-42
                  </Size>
                )}
                {product?.socksM && (
                  <Size
                    $active={selectedSize === "43-46"}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedSize("43-46");
                    }}
                  >
                    43-46
                  </Size>
                )}
              </Sizes>
            )}
          </Info>
          {!admin && product?.isInStock && (
            <ButtonContainer>
              <AddToCart
                $disabled={!selectedSize}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!selectedSize) {
                    setShake(true);
                    setTimeout(() => setShake(false), 500);
                    return;
                  }
                  flyToCart(e);
                  dispatch(
                    addToCart({
                      ...product,
                      quantity,
                      selectedSize,
                    })
                  );
                  setShowToast(true);
                  setSelectedSize(null);
                }}
              >
                <FiShoppingCart size={20} />
                Dodaj u korpu
              </AddToCart>
            </ButtonContainer>
          )}
        </Card>
      )}

      {adminToast && (
        <AdminToast
          message={adminToast.message}
          type={adminToast.type}
          onClose={() => setAdminToast(null)}
        />
      )}

      <AnimatePresence>
        {modalOpen && (
          <ModalOverlay
            as={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => {
              setModalOpen(false);
            }}
          >
            <ModalBox
              style={{ alignItems: "center" }}
              as={motion.div}
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.25 }}
              onClick={(e) => e.stopPropagation()}
            >
              <CloseBtn
                onClick={(e) => {
                  e.stopPropagation();
                  setModalOpen(false);
                }}
              >
                <FaTimes />
              </CloseBtn>

              <ImageContainer>
                {(product?.photos?.length || product?.thumbnailImage) &&
                  !imageLoaded && (
                    <LoaderOverlay style={{ borderRadius: "1.2rem" }}>
                      <l-line-spinner
                        size="16"
                        stroke="2"
                        speed="1"
                        color="#fe5f55"
                      />
                    </LoaderOverlay>
                  )}

                {product?.photos?.length ? (
                  <ModalImage
                    src={`${FRONT_URL}/images/${product?.photos[0]?.photoName}`}
                    alt={product?.name}
                    onLoad={() => setImageLoaded(true)}
                    onError={() => setImageLoaded(true)}
                  />
                ) : product?.thumbnailImage ? (
                  <ModalImage
                    src={`${FRONT_URL}/thumbnails/${product?.thumbnailImage}`}
                    alt={product?.name}
                    onLoad={() => setImageLoaded(true)}
                    onError={() => setImageLoaded(true)}
                  />
                ) : (
                  <ImagePlaceholder iconSize={48} />
                )}
              </ImageContainer>

              <InfoContainer>
                <StyledH2
                  onClick={(e) => {
                    if (!admin) {
                      e.stopPropagation();
                      navigate(`/proizvod/${product?.id}`);
                      setModalOpen(false);
                      setTimeout(() => {
                        window.scrollTo(0, 0);
                      }, 300);
                    }
                  }}
                >
                  {product?.name}
                </StyledH2>
                <StyledP>{product?.description}</StyledP>
                {product.isInStock && (
                  <QuantityRow>
                    <QuantityControl onClick={decrementQty}>–</QuantityControl>
                    <QuantityValue>{quantity}</QuantityValue>
                    <QuantityControl onClick={incrementQty}>+</QuantityControl>
                  </QuantityRow>
                )}
                <Stock
                  $available={product?.isInStock}
                  style={{ marginTop: "1rem" }}
                >
                  {product?.comingSoon
                  ? "Uskoro"
                  : product?.isInStock
                  ? "Na stanju"
                  : "Rasprodato"}
                </Stock>
                {product?.comingSoon ? (
                  <Price>Uskoro u prodaji</Price>
                ) : product?.salePrice ? (
                  <PriceRow>
                    <PriceOld>{priceFormatter(product?.price)} RSD</PriceOld>
                    <Price>{priceFormatter(product?.salePrice)} RSD</Price>
                  </PriceRow>
                ) : (
                  <Price>{priceFormatter(product?.price)} RSD</Price>
                )}

                {product.isInStock && (
                  <>
                    <Label>Veličina:</Label>
                    <Sizes>
                      {product?.sizeS && (
                        <Size
                          $active={selectedSize === "S"}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedSize("S");
                          }}
                        >
                          S
                        </Size>
                      )}
                      {product?.sizeM && (
                        <Size
                          $active={selectedSize === "M"}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedSize("M");
                          }}
                        >
                          M
                        </Size>
                      )}
                      {product?.sizeL && (
                        <Size
                          $active={selectedSize === "L"}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedSize("L");
                          }}
                        >
                          L
                        </Size>
                      )}
                      {product?.sizeXL && (
                        <Size
                          $active={selectedSize === "XL"}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedSize("XL");
                          }}
                        >
                          XL
                        </Size>
                      )}
                      {product?.sizeXL && (
                        <Size
                          $active={selectedSize === "XL"}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedSize("XL");
                          }}
                        >
                          XL
                        </Size>
                      )}
                      {product?.sizeSM && (
                        <Size
                          $active={selectedSize === "S/M"}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedSize("S/M");
                          }}
                        >
                          S/M
                        </Size>
                      )}
                      {product?.sizeLXL && (
                        <Size
                          $active={selectedSize === "L/XL"}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedSize("L/XL");
                          }}
                        >
                          L/XL
                        </Size>
                      )}
                      {product?.socksS && (
                        <Size
                          $active={selectedSize === "39-42"}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedSize("39-42");
                          }}
                        >
                          39-42
                        </Size>
                      )}
                      {product?.socksM && (
                        <Size
                          $active={selectedSize === "43-46"}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedSize("43-46");
                          }}
                        >
                          43-46
                        </Size>
                      )}
                    </Sizes>
                  </>
                )}
                <Label>Boja:</Label>
                <ColorDot2
                  $color={product?.color}
                  style={{ marginBottom: 20 }}
                />

                <AddToCartModal
                  disabled={!product?.isInStock || !selectedSize}
                  onClick={(e) => {
                    e.stopPropagation();
                    dispatch(
                      addToCart({
                        ...product,
                        quantity,
                        selectedSize,
                      })
                    );
                    setShowToast(true);
                    setSelectedSize(null);
                    setQuantity(1);
                  }}
                >
                  <FiShoppingCart size={20} />
                  Dodaj u korpu
                </AddToCartModal>
              </InfoContainer>
            </ModalBox>
          </ModalOverlay>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {openRemoveModal && (
          <ModalOverlay
            as={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpenRemoveModal(false)}
          >
            <ConfirmBox
              as={motion.div}
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
            >
              <CloseBtn onClick={() => setOpenRemoveModal(false)}>
                <FaTimes />
              </CloseBtn>
              <h3>Potvrdi brisanje</h3>
              <p>Da li si siguran da želiš da obrišeš ovaj proizvod?</p>
              <ModalActions>
                <Cancel
                  onClick={() => {
                    if (!products.deleteLoading) {
                      setOpenRemoveModal(false);
                    }
                  }}
                >
                  Odustani
                </Cancel>
                {!products.deleteLoading ? (
                  <Confirm
                    onClick={() => {
                      dispatch(deleteProduct(product?.id))
                        .unwrap()
                        .then(() => {
                          setOpenRemoveModal(false);
                          setAdminToast({
                            type: "success",
                            message: "Proizvod je obrisan.",
                          });
                        })
                        .catch((err) =>
                          setAdminToast({
                            type: "error",
                            message:
                              err.message || "Greška pri brisanju proizvoda.",
                          })
                        );
                    }}
                  >
                    Obriši
                  </Confirm>
                ) : (
                  <Confirm $disabled>
                    Obriši
                    <l-line-spinner
                      size="16"
                      stroke="2"
                      speed="1"
                      color="#fff"
                    />
                  </Confirm>
                )}
              </ModalActions>
            </ConfirmBox>
          </ModalOverlay>
        )}
      </AnimatePresence>

      {showToast && (
        <CustomToast
          message="Proizvod je dodat u korpu!"
          onClose={() => setShowToast(false)}
        />
      )}
    </>
  );
};

export default ProductCard;

const ConfirmBox = styled.div`
  background: #1a1a1a;
  padding: 3rem 2rem;
  border-radius: 1.6rem;
  max-width: 400px;
  width: 100%;
  color: white;
  position: relative;
  text-align: center;

  h3 {
    font-size: 2rem;
    margin-bottom: 1rem;
    color: #fe5f55;
    font-family: "Poppins-SemiBold";
  }

  p {
    font-size: 1.3rem;
    margin-bottom: 2rem;
    font-family: "Poppins-Medium";
    color: #ccc;
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
`;

const Cancel = styled.button`
  padding: 0.8rem 2rem;
  border-radius: 0.8rem;
  background: #333;
  color: white;
  border: none;
  font-family: "Poppins-SemiBold";
  cursor: pointer;

  &:hover {
    background: #444;
  }
`;

const Confirm = styled.button`
  padding: 0.8rem 2rem;
  border-radius: 0.8rem;
  background: #fe5f55;
  color: white;
  border: none;
  cursor: pointer;

  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.65rem;
  font-size: 1.4rem;
  font-family: "Poppins-SemiBold";
  cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};

  &:hover {
    background: #ff6a5e;
  }
`;

const Column = styled.div`
  display: flex;
  margin-bottom: 1rem;
  flex-direction: column;
`;

const Card = styled.div`
  background: var(--bg2, #17171c);
  border: 1px solid var(--line, rgba(255, 255, 255, 0.07));
  border-radius: 0.6rem;
  overflow: hidden;
  width: 100%;
  aspect-ratio: 3 / 4;
  position: relative;
  cursor: pointer;
  transition: transform 0.45s var(--ease, ease),
    border-color 0.45s var(--ease, ease),
    box-shadow 0.45s var(--ease, ease);

  &:hover {
    transform: translateY(-6px);
    border-color: rgba(254, 95, 85, 0.45);
    box-shadow: 0 2.4rem 4.8rem -2rem rgba(0, 0, 0, 0.8),
      0 0 0 1px rgba(254, 95, 85, 0.15);

    img {
      transform: scale(1.07);
    }
  }
`;

const ButtonContainer = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 3;
  padding: 0 1.6rem 1.6rem;
  transform: translateY(110%);
  transition: transform 0.4s var(--ease, ease);

  ${Card}:hover & {
    transform: translateY(0);
  }

  @media (hover: none) {
    transform: translateY(0);
  }
`;
const StyledP = styled.span`
  font-size: 1.4rem;
  font-family: "Poppins-Regular";
  color: #aaa;

  display: -webkit-box;
  -webkit-line-clamp: 5; /* maks. 3 reda */
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: justify;
  max-width: 40rem;
`;
const QuantityRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin: 2rem 0rem;
`;
const ImageContainer = styled.div`
  width: 60%;
  height: 100%;
  position: relative;
  @media (max-width: 768px) {
    width: 30rem;
    height: 30rem;
    align-items: center;
    justify-content: center;
    display: flex;
  }
  @media (max-width: 400px) {
    width: 100%;
    height: 100%;
  }
`;
const ModalImage = styled.img`
  border-radius: 1.2rem;
  height: auto;
  max-width: 100%;
  @media (max-width: 768px) {
    aspect-ratio: 1/1;
    width: 30rem;
  }
  @media (max-width: 400px) {
    width: 100%;
  }
`;
const QuantityControl = styled.button`
  width: 3.2rem;
  height: 3.2rem;
  font-size: 1.4rem;
  border: none;
  background: #2a2a2a;
  color: white;
  border-radius: 0.6rem;
  cursor: pointer;
`;

const QuantityValue = styled.div`
  min-width: 2.4rem;
  text-align: center;
  font-size: 1.4rem;
  font-family: "Poppins-Medium";
  color: white;
`;
const Icons = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 1rem;
  position: absolute;
  top: 1.3rem;
  right: 4.4rem;
  z-index: 3;
  color: #fff;
`;
const StyledH2 = styled.h2`
  color: #fe5f55;
  font-size: 2.2rem !important;
  font-family: "Poppins-Bold";
  margin-bottom: 0.5rem !important;
  line-height: 2.2rem;
  cursor: pointer;
  &:hover {
    color: #fe8f88;
  }
`;

export const Image = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: all 0.3s ease-in-out;
  &:hover {
    transform: scale(1.1);
  }
`;
const InfoContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  @media (max-width: 768px) {
    justify-content: center;
    align-items: center;
    text-align: center;
  }
`;
const ImageWrapper = styled.div`
  position: absolute;
  inset: 0;
  background: var(--bg1, #101013);
  overflow: hidden;

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    z-index: 1;
    background: linear-gradient(
      0deg,
      rgba(6, 6, 8, 0.94) 0%,
      rgba(6, 6, 8, 0.55) 22%,
      transparent 48%
    );
    pointer-events: none;
  }
`;

const Label = styled.div`
  font-size: 1.2rem;
  font-family: "Poppins-Medium";
  margin-bottom: 0.5rem;
  margin-top: 1rem;
`;
const Badge = styled.div`
  position: absolute;
  top: 1.2rem;
  left: 1.2rem;
  z-index: 3;
  background: var(--red, #fe5f55);
  color: var(--bg0, #0a0a0c);
  font-size: 1rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  font-family: "Poppins-Bold";
  padding: 0.5rem 1rem;
  border-radius: 2px;
`;

const Edit = styled(FiEdit)`
  cursor: pointer;
  transition: all 0.3s ease-in-out;
  &:hover {
    color: #fe5f55;
    transform: scale(1.1);
  }
`;

const Trash = styled(FiTrash2)`
  cursor: pointer;
  transition: all 0.3s ease-in-out;
  &:hover {
    color: #fe5f55;
    transform: scale(1.1);
  }
`;


const Info = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2;
  padding: 1.6rem 1.6rem 1.4rem;
  color: var(--text, #f4f2ee);
  transition: transform 0.4s var(--ease, ease);

  ${Card}:hover &:not(:last-child) {
    transform: translateY(-5.4rem);
  }

  @media (hover: none) {
    &:not(:last-child) {
      transform: translateY(-5.4rem);
    }
  }
`;

const Title = styled.h3`
  font-size: 1.6rem;
  line-height: 1.3;
  margin-bottom: 0.6rem;
  font-family: "Poppins-SemiBold";
  text-shadow: 0 0.2rem 1.2rem rgba(0, 0, 0, 0.6);
`;

const Description = styled.span`
  display: none;
`;

const Price = styled.span`
  font-size: 1.7rem;
  font-family: "Anton", sans-serif;
  font-weight: 400;
  letter-spacing: 0.02em;
  color: var(--red, #fe5f55);
`;
const PriceOld = styled.span`
  font-size: 1.2rem;
  font-family: "Poppins-Regular";
  color: var(--muted2, #5c5a63);
  text-decoration: line-through;
`;

const PriceRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
`;

const shakeAnim = keyframes`
  0%, 100% { transform: translateX(0); }
  20% { transform: translateX(-0.6rem); }
  40% { transform: translateX(0.6rem); }
  60% { transform: translateX(-0.4rem); }
  80% { transform: translateX(0.4rem); }
`;

const Sizes = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: ${(props) => (props.$admin ? "1.5rem" : "1rem")};

  ${(props) =>
    props.$shake &&
    css`
      animation: ${shakeAnim} 0.45s ease;

      & > div {
        border-color: var(--red, #fe5f55);
      }
    `}
`;

const Size = styled.div`
  padding: 0.5rem 1rem;
  border: 1px solid
    ${({ $active }) =>
      $active ? "var(--red, #fe5f55)" : "var(--line2, rgba(255,255,255,0.14))"};
  color: ${({ $active }) =>
    $active ? "var(--bg0, #0a0a0c)" : "var(--muted, #8e8c94)"};
  background: ${({ $active }) =>
    $active ? "var(--red, #fe5f55)" : "transparent"};
  border-radius: 2px;
  cursor: pointer;
  font-size: 1.1rem;
  letter-spacing: 0.06em;
  transition: 0.2s;
  background: ${({ $active }) =>
    $active ? "var(--red, #fe5f55)" : "rgba(10, 10, 12, 0.55)"};
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  font-family: ${({ $active }) =>
    $active ? '"Poppins-SemiBold"' : '"Poppins-Regular"'};
  display: flex;
  align-items: center;
  justify-content: center;
  user-select: none;
`;

const ColorDot2 = styled.div`
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  border: ${({ $active }) =>
    $active ? "2px solid #fe5f55" : "2px solid #fff"};
  background: ${({ $color }) => $color};
  cursor: pointer;
`;
const ColorDot = styled.div`
  width: 1.8rem;
  height: 1.8rem;
  border-radius: 50%;
  border: ${({ $active }) =>
    $active ? "2px solid #fe5f55" : "2px solid rgba(255, 255, 255, 0.85)"};
  background: ${({ $color }) => $color};
  cursor: pointer;
  position: absolute;
  top: 1.4rem;
  right: 1.4rem;
  z-index: 3;
`;

const Stock = styled.div`
  font-family: "Poppins-Medium";
  font-size: 1rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ $available }) =>
    $available ? "#7dd490" : "var(--red, #fe5f55)"};
`;

const AddToCart = styled.button`
  background: ${(props) =>
    props.$small
      ? "rgba(10, 10, 12, 0.6)"
      : props.$disabled
        ? "rgba(23, 23, 28, 0.85)"
        : "var(--red, #fe5f55)"};

  color: ${(props) =>
    props.$small
      ? "var(--text, #f4f2ee)"
      : props.$disabled
        ? "var(--muted, #8e8c94)"
        : "var(--bg0, #0a0a0c)"};
  border: 1px solid
    ${(props) =>
      props.$small
        ? "rgba(255, 255, 255, 0.25)"
        : props.$disabled
          ? "var(--line2, rgba(255,255,255,0.14))"
          : "transparent"};

  .ar {
    font-style: normal;
    transition: transform 0.3s var(--ease, ease);
  }

  &:hover .ar {
    transform: translateX(0.4rem);
  }
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  padding: 1.2rem 1.5rem;
  border-radius: 0.4rem;
  box-shadow: ${(props) =>
    props.$disabled || props.$small
      ? "none"
      : "0 0.8rem 2.4rem -0.8rem rgba(254, 95, 85, 0.6)"};
  font-family: "Anton", sans-serif;
  font-weight: 400;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-size: 1.3rem;
  cursor: pointer;
  width: 100%;
  transition: background 0.3s;

  display: flex;
  align-items: center;
  flex-direction: row;
  position: relative;
  overflow: hidden;
  justify-content: center;

  gap: 1rem;

  svg {
    width: 1.6rem;
    height: 1.6rem;
    transition: transform 0.3s var(--ease, ease);
  }

  &:hover {
    background: ${(props) =>
      props.$small
        ? "rgba(10, 10, 12, 0.6)"
        : props.$disabled
          ? "rgba(23, 23, 28, 0.85)"
          : "var(--red-deep, #e04a40)"};

    svg {
      transform: ${(props) =>
        props.$disabled ? "none" : "translateY(-0.2rem) rotate(-8deg)"};
    }
  }

  ${(props) =>
    props.$small &&
    css`
      &:hover {
        border-color: var(--red, #fe5f55);
        color: var(--red, #fe5f55);
      }
    `}

  &:disabled {
    background: var(--bg3, #1e1e24);
    color: var(--muted2, #5c5a63);
    cursor: not-allowed;
  }
`;

// Modal styles
const AddToCartModal = styled.button`
  background: var(--red, #fe5f55);
  max-width: 20rem;
  color: var(--bg0, #0a0a0c);
  border: none;
  padding: 1.2rem 1.5rem;
  border-radius: 3px;
  font-family: "Anton", sans-serif;
  font-weight: 400;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-size: 1.3rem;
  cursor: pointer;
  width: 100%;
  transition: background 0.3s;

  display: flex;
  align-items: center;
  flex-direction: row;
  position: relative;
  overflow: hidden;
  justify-content: center;

  gap: 1rem;

  svg {
    width: 1.6rem;
    height: 1.6rem;
    transition: transform 0.3s var(--ease, ease);
  }

  &:hover {
    background: var(--red-deep, #e04a40);

    svg {
      transform: translateY(-0.2rem) rotate(-8deg);
    }
  }

  &:disabled {
    background: var(--bg3, #1e1e24);
    color: var(--muted2, #5c5a63);
    cursor: not-allowed;
  }
`;
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 1999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
`;

const ModalBox = styled.div`
  background: #1a1a1a;
  padding: 24px;
  border-radius: 16px;
  width: 100%;
  max-width: 860px;
  max-height: 90vh;
  padding-left: 0rem;
  padding-top: 0rem;
  padding-bottom: 0rem;

  overflow: hidden;
  position: relative;
  color: white;
  display: flex;
  gap: 24px;
  pointer-events: auto;

  @media (max-width: 768px) {
    flex-direction: column;
    max-height: 95vh;
    padding: 16px;
  }
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  color: #fff;
  border-radius: 50%;
  font-size: 1.6rem;
  width: 3rem;
  height: 3rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0px 0px 5px rgba(0, 0, 0, 0.25);
  -webkit-backdrop-filter: blur(4px);
  background-color: rgba(0, 0, 0, 0.25);
  backdrop-filter: blur(4px);
  transition: background 0.3s;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
    color: #ff6a5e;
  }
`;

const LoaderOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: #1a1a1a;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fe5f55;
  font-size: 1.2rem;
  font-family: "Poppins-SemiBold";
  z-index: 2;
  pointer-events: none;
`;
